/**
 * loudness — the ONE play-time levelling mechanism (Tom 2026-10-12, ask #139).
 *
 * Born in the spliced-intro assembler (#699/#728/#739) and lifted here so every
 * clip the player decodes is brought to the same voiced loudness: target
 * phrases, known lines, presentation intros, pods, listening. Uneven
 * recordings stop mattering and nothing needs re-recording.
 *
 * Where it runs: `bytesToWavBlob` (cache/wav.ts), the decode every played clip
 * already goes through — main loop (SimplePlayer.resolveAudioUrl), the
 * AudioController source (intros, pods, dialogues) and the listening overlay
 * all read AudioCache.getWavBlobUrl. So levelling costs no extra fetch, no
 * extra decode and no extra hop between clips; the prewarm one cycle ahead
 * absorbs the arithmetic.
 *
 * Pure functions over PcmClip; nothing here touches the network or the DB.
 */
import type { PcmClip } from './wav'

export const toDb = (x: number): number => 20 * Math.log10(Math.max(x, 1e-9))

/**
 * Where every clip lands (voiced RMS, dBFS). Measured 2026-10-12 over the first
 * 30 spa_for_eng LEGOs: the target-language clips sit at about -14 dBFS, so
 * levelling to it moves the typical clip very little and the outliers a lot.
 */
export const CLIP_TARGET_DB = -14

export const PEAK_CEILING = 0.97
/** +15.6 dB: never amplify a near-silent clip into noise. */
export const MAX_GAIN = 6

/** Voiced-frame loudness: 20 ms frames, gate 20 dB under the loudest frame. */
export function voicedRms(clip: PcmClip): number {
  const frame = Math.max(1, Math.round(clip.sampleRate * 0.02))
  const n = clip.channels[0].length
  const ms: number[] = []
  for (let i = 0; i + frame <= n || (i < n && ms.length === 0); i += frame) {
    const end = Math.min(n, i + frame)
    let sum = 0
    for (const ch of clip.channels) for (let j = i; j < end; j++) sum += ch[j] * ch[j]
    ms.push(sum / ((end - i) * clip.channels.length))
  }
  let max = 0
  for (const m of ms) if (m > max) max = m
  const gate = max * 0.01 // -20 dB in power
  let total = 0, count = 0
  for (const m of ms) if (m >= gate && m > 0) { total += m; count++ }
  return count ? Math.sqrt(total / count) : 0
}

export const scaleClip = (c: PcmClip, g: number): PcmClip => ({
  sampleRate: c.sampleRate,
  channels: c.channels.map((ch) => { const o = new Float32Array(ch.length); for (let j = 0; j < ch.length; j++) o[j] = ch[j] * g; return o }),
})

/**
 * Transparent peak limiter: where a sample would pass `ceiling`, the gain dips
 * smoothly over ±4 ms around it, so a lone transient is trimmed locally and the
 * rest of the clip is untouched (a global scale-down would lower the whole
 * clip by however peaky its loudest moment was). Guarantees |x| ≤ ceiling.
 */
export function limitPeaks(clip: PcmClip, ceiling: number = PEAK_CEILING): PcmClip {
  const n = clip.channels[0].length
  const L = Math.max(1, Math.round(clip.sampleRate * 0.004))
  const need = new Float32Array(n).fill(1)
  let any = false
  for (let i = 0; i < n; i++) {
    let a = 0
    for (const ch of clip.channels) { const v = Math.abs(ch[i]); if (v > a) a = v }
    if (a > ceiling) { need[i] = ceiling / a; any = true }
  }
  if (!any) return clip
  const low = new Float32Array(n).fill(1)
  for (let i = 0; i < n; i++) {
    if (need[i] >= 1) continue
    for (let j = Math.max(0, i - L); j <= Math.min(n - 1, i + L); j++) if (need[i] < low[j]) low[j] = need[i]
  }
  const gain = new Float32Array(n)
  let acc = 0
  const w = 2 * L + 1
  for (let j = 0; j < Math.min(n, L); j++) acc += low[j]
  for (let i = 0; i < n; i++) {
    if (i + L < n) acc += low[i + L]
    if (i - L - 1 >= 0) acc -= low[i - L - 1]
    gain[i] = acc / w // window past the edges counts as 1 (no reduction)
  }
  // edges: the missing window part counts as unity gain
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - L), hi = Math.min(n - 1, i + L)
    const missing = w - (hi - lo + 1)
    if (missing) gain[i] = (gain[i] * w + missing) / w
  }
  // the smoothing (esp. the edge fix-up) can leave a sample above its own need: clamp so |x| ≤ ceiling holds at every sample
  for (let i = 0; i < n; i++) if (gain[i] > need[i]) gain[i] = need[i]
  return {
    sampleRate: clip.sampleRate,
    channels: clip.channels.map((ch) => { const o = new Float32Array(n); for (let i = 0; i < n; i++) o[i] = ch[i] * gain[i]; return o }),
  }
}

/**
 * Bring a clip to `targetDb` (voiced RMS) and keep it under the peak ceiling.
 * The limiter lowers the level a little where it bites, so the gain is
 * re-solved a few times. `maxGain` caps the lift. A global gain, so any
 * internal shape (an intro's LEGO-over-frame lift) is kept exactly.
 */
export function normaliseLevel(clip: PcmClip, targetDb: number = CLIP_TARGET_DB, maxGain: number = Infinity): PcmClip {
  const level = voicedRms(clip)
  if (level <= 0) return clip
  const want = 10 ** (targetDb / 20)
  let g = Math.min(maxGain, want / level)
  let out = limitPeaks(scaleClip(clip, g))
  for (let k = 0; k < 4 && g < maxGain; k++) {
    const l = voicedRms(out)
    if (l <= 0 || Math.abs(toDb(l) - targetDb) < 0.05) break
    g = Math.min(maxGain, g * (want / l))
    out = limitPeaks(scaleClip(clip, g))
  }
  return out
}

/**
 * `?level=off` (or localStorage `ssi_clip_levelling` = 'off') plays every clip
 * as recorded — for judging the levelling by ear. Read per decode; decodes are
 * memoised per clip, so change it with a reload.
 */
export function clipLevellingOn(): boolean {
  try {
    if (new URLSearchParams(globalThis.location?.search ?? '').get('level') === 'off') return false
    if (globalThis.localStorage?.getItem('ssi_clip_levelling') === 'off') return false
  } catch { /* storage / location unavailable — on */ }
  return true
}

/**
 * Level one decoded clip for playback. Never throws: if anything goes wrong
 * the clip plays as recorded — nothing interrupts learning.
 */
export function levelClip(clip: PcmClip): PcmClip {
  if (!clipLevellingOn()) return clip
  try {
    if (!clip.channels.length || !clip.channels[0].length) return clip
    return normaliseLevel(clip, CLIP_TARGET_DB, MAX_GAIN)
  } catch (err) {
    console.warn('[loudness] levelling failed, playing the clip as recorded:', (err as Error)?.message)
    return clip
  }
}
